import { state } from '@angular/animations';
import { environment } from 'environments/environment';
export class AppConsts {

    static readonly tenancyNamePlaceHolderInUrl = '{TENANCY_NAME}';

       /**
     * IHandleError.message_Info marker returned when the backend found an entity's SharePoint/OneDrive
     * folder link dead and re-pointed it at a live folder mid-request. Whoever receives it must re-read the
     * folder details: the folder id / drive id / web URL it holds (e.g. an "Open in SharePoint" link) are
     * stale. Must match HandleInfoCodes.FolderRelinked on the backend.
     */
    static readonly folderRelinkedInfo = 'folder-relinked';

    /**
     * dataService.refreshContacts payload meaning "an edit-client modal (person or company) was closed",
     * as opposed to its true / false payloads ("contacts are importing" / "reload after a save"). The
     * clients list reloads for it AND drops the row highlight that opening the client left behind - a save
     * must NOT drop it, because the modal is still open over the list.
     *
     * Emitted only by MainLayoutComponent's closeEditContactEmit / closeEditCompanyEmit; contact-list is
     * the channel's only subscriber.
     */
    static readonly clientModalClosed = 'client-modal-closed';

    /**
     * Master switch for the Task Dependencies feature (Blocked by / Next, auto-shift,
     * blocked filter, ⊘ icons, unblock notifications, etc.). Set to FALSE to fully
     * hide and disable the feature for production until it's finished and tested.
     * Flip to TRUE to re-enable everything in one place.
     *
     * Gates: the Dependencies tab on the task panel, the Blocked filter pill in
     * My Day, the ⊘ blocked icons (project list / calendar / search), the
     * parent-task delete confirmation, the cross-project move confirmation, and
     * the FE isTaskBlocked() routing. The backend has a matching flag
     * (TaskAppService.DependenciesFeatureEnabled) that gates the woven-in hooks.
     */
    static readonly dependenciesFeatureEnabled = true;

    /**
     * Master switch for dependencies BETWEEN TASK TEMPLATES inside a project template — the
     * step-2 grid's "Blocked by" / "Next" columns and the task composer's Dependencies tab,
     * where one template task can be made to wait on another. Set to FALSE to put the feature
     * on hold: a template task then keeps only its own manual start offset ("Starts"), exactly
     * as before the feature existed. Flip to TRUE to bring it all back in one place.
     *
     * ⚠️ NOT the same flag as `dependenciesFeatureEnabled` above, which governs dependencies
     * between REAL tasks (My Day, the task panel, the calendar, the timeline). The two are
     * independent: this one being off does not touch that feature.
     *
     * Gates: the two dependency grid columns in wizard step 2 (and with them their hover
     * cards), the Dependencies tab on the template task composer, the derived-start schedule
     * (`liveEdges` in template-schedule.ts, which is what makes "Starts" fall back to the
     * stored `daysAfter`), and the legacy ParentId→edge lift on load.
     *
     * ⚠️ FE-ONLY, DELIBERATELY. There is no backend sibling and there must not be a change in
     * what the client SENDS. `Vf_Update_Project_Template` merges the graph with
     * `WHEN NOT MATCHED BY SOURCE ... IsDeleted = 1`, so a save that omits the edges wipes
     * them. The edges are loaded and posted straight back while hidden, so an existing
     * template keeps its graph and keeps producing the same dates when a project is created
     * from it. On hold means invisible, not deleted.
     */
    static readonly templateDependenciesFeatureEnabled = false;

    /**
     * Master switch for the Custom Project Label (Labels & Terminology) feature. A tenant admin
     * can rename "Project"/"Projects" to match their industry (Cases, Matters, Jobs, ...) in
     * Workspace > Settings, and the new wording propagates across every UI surface in real time.
     * Set to FALSE to hide the Settings field and force every surface back to "Project"/"Projects"
     * in one place (kill-switch). The backend has a matching flag
     * (ProjectLabelConsts.FeatureEnabled) — keep them in sync.
     *
     * Read everywhere through AppComponentBase.projectLabel / projectLabelPlural (+ the lowercase
     * variants), which fall back to the defaults when this is false.
     */
    static readonly customProjectLabelFeatureEnabled = true;

    /**
     * Master switch for the Timeline (Gantt) project-view tab — the visual project
     * schedule with drag-to-reschedule, dependency arrows, zoom levels (Days/Weeks/Months),
     * grouping, filtering and risk surfacing. Set to FALSE to fully hide the Timeline tab
     * from the project view tab strip (kill-switch / progressive rollout). Flip to TRUE to
     * enable. See TIMELINE_GANTT_PLAN.md (repo root) for the full build plan.
     *
     * Gates: the Timeline tab button + pane in view-project-modal. Reuses the existing
     * task + Task Dependencies backend (dependenciesFeatureEnabled must also be on for the
     * dependency arrows / connect / cascade to work).
     */
    static readonly timelineV1FeatureEnabled = true;

    /**
     * Master switch for automatic My Day day-rollover (DayRolloverService). When TRUE, a long-lived
     * tab detects that the work date has changed and silently re-seeds My Day — dates, meeting bar,
     * task list, new-task defaults — instead of relying on the check-in popup's full page reload.
     * Also suppresses that popup.
     *
     * Set to FALSE (the ship default) for today's behaviour exactly: the popup appears and its
     * "Refresh my workspace" button reloads the page, and DayRolloverService stays completely inert
     * (no timers, no session calls, no subscriptions).
     *
     * FE-ONLY flag — there is NO backend sibling. Rollover works by re-calling the existing
     * Session/GetCurrentLoginInformations via AppSessionService.init(), which is already idempotent
     * per work date (Vf_Create_User_CheckInOut only inserts when the computed WorkDate has no
     * un-deleted CheckIn row), so nothing new is needed server-side.
     *
     * ⚠️ The check-in popup only ever fires in PROD — CheckinReminderWorker is registered inside
     * !DebugHelper.IsDebug (VineforceWebHostModule.cs:94). So popup suppression cannot be validated
     * on a local Debug build; the rollover behaviour itself can.
     *
     * See CHECKIN_ELIMINATION_PLAN.md §3 and CHECKIN_REMOVAL_TIERS.md (tier T2/T3) at the repo root.
     */
    static readonly dayRolloverV1FeatureEnabled = true;

    /**
     * Master switch for the Unified Task Detail — Conversations redesign (task detail
     * harmonization). Collapses the task edit modal's three separate history surfaces
     * (email thread / notes editor / Comments tab) into ONE fixed vertical column for every
     * task type: notes on top, then an interleaved Conversations stream (emails + comments +
     * system pills), with anchored comments, an AI conversation summary, and collapsible notes.
     * Set to FALSE to render the modal exactly as today (legacy panes); TRUE mounts the
     * redesigned left column. See TASK_CONVERSATIONS_PLAN.md (repo root) for the full build plan.
     *
     * FE-ONLY flag — there is NO backend sibling. The Phase-4 anchored-comment endpoints/columns
     * (Task.ResolveComment + the TrTaskComment anchor/resolve columns) stay dormant while the FE
     * doesn't call them, so they need no gate of their own. Requires emailAutomationFeatureEnabled
     * = TRUE for the modal's email bar / reply composer / "Watching for a reply" follow-up card.
     *
     * Currently TRUE for TEST/QA — phases 1–7 built + runtime-verified nearly end-to-end.
     * ⚠️ Before production GA, flip back to FALSE until sign-off. Full anchored-comment
     * resolve/reopen also needs migration 20260728133612_Added_TaskCommentAnchorColumns applied to
     * the target DB (apply via the deploy path, NOT the EF CLI — that targets prod).
     */
    static readonly taskConversationsFeatureEnabled = true;

    /**
     * Master switch for the Email Automation feature (watch M365 inboxes -> tasks/leads/filed docs,
     * the task email bar + reply composer, and the shared-inbox manager). Set to FALSE to fully hide
     * every Email-automation surface in one place (kill-switch / progressive rollout): the Settings
     * "Email automation" tab + its profile-popover nav item, and the My Day first-time callout.
     * Per-tenant on/off still lives in the tenant Setting (App.EmailAutomation.IsEnabled) surfaced as
     * the master toggle inside that tab; this const is the build-time gate above it.
     */
    static readonly emailAutomationFeatureEnabled = true;

    /**
     * Frontend rollout switch for the paste-only email-signature release. TRUE exposes the Profile import
     * screen, saved signature/fallback in reply composers, and signature assembly on send. FALSE hides
     * those surfaces, suppresses signature API reads, and sends the authored composer body unchanged.
     *
     * FE-ONLY flag — there is no backend counterpart. The Profile signature endpoints and per-user data
     * remain available while this switch is off. Keep every Profile and composer signature surface gated
     * by this constant.
     */
    static readonly emailSignatureFeatureEnabled = true;

    /**
     * Master kill-switch for the Calendar & Events feature (settings tab + master switch, event
     * types, name formats, sharing, the workspace/project calendars and Outlook sync). Set to FALSE
     * to fully hide the feature — the Workspace > Calendar settings tab, the Calendar nav item, and
     * the project Calendar tabs all disappear regardless of any tenant's master switch. Flip to TRUE
     * for progressive rollout. The backend has a matching flag (CalendarConsts.FeatureEnabled) —
     * keep them in sync.
     *
     * Two gates stack: this kill-switch AND the per-tenant master switch (E2,
     * CalendarSettingsDto.isEnabled) must BOTH be on for any calendar surface to appear.
     */
    static readonly calendarV1FeatureEnabled = true;

    /**
     * E13 P1 rollout flag for re-sourcing the My-Day meeting bar (+ tomorrow / widget / planner reads)
     * off the legacy TrUserEvents read (Calendar/GetMyMeetings) and onto the SrcEvents-backed read
     * (Event/GetMyMeetingsBar). Default FALSE: the bar keeps using the legacy read as a fallback until the
     * SrcEvents read is built and verified on dev (timezone/field-contract parity — see
     * MEETING_BAR_OUTLOOK_PLAN.md R1/R2). Flip to TRUE only after the new read is confirmed. Purely a
     * data-source swap — the bar UI, conflict logic and TZ handling are unchanged. ADDITIVE: the legacy
     * read stays intact behind this flag.
     */
    static readonly meetingBarV2FeatureEnabled = true;

    /**
     * Master switch for the "Next event" project-list story (NEXT_EVENT_COLUMN_SPEC at repo root):
     * the opt-in computed column (soonest upcoming event per project, date/time + type chip), its
     * Today/This week/Next week/Later/No-upcoming group-by buckets, its type/time-window filters,
     * the event-cell click-through to the read-only event panel, and the clickable Client cell
     * (contact/company editor with Notes). Set to FALSE to fully hide all of it in one place.
     * Stacks with calendarV1FeatureEnabled — the calendar epic must also be on (the column reads
     * SrcEvents via Event/GetProjectNextEvents, which is itself soft-gated on the tenant policy).
     */
    static readonly nextEventColumnFeatureEnabled = true;

    /**
     * Master switch for "Edit in grid view" (EDIT_IN_GRID_VIEW_PLAN.md at the repo root): the
     * spreadsheet-style inline editing mode on the project task list (Tasks tab → three-dots →
     * Edit in grid view) and the all-projects list (toolbar button). Set to FALSE to hide both entry
     * points, so both lists behave exactly as they did before the feature.
     *
     * The backend has a matching flag (`GridEditConsts.FeatureEnabled`) guarding the one new endpoint
     * the grid needed, `Task/UpdateTaskName` — keep the two in sync. Every other field the grid writes
     * goes through an endpoint the bulk-edit actions already use, so those are not gated.
     */
    static readonly gridEditFeatureEnabled = true;

    /**
     * Master switch for "My Day — Lead, Client Communication, Status & Completion"
     * (MYDAY_LEAD_COMMS_PLAN.md at the repo root, row CFG-01). FALSE in checked-in code until the P8 QA
     * flip; while it is off every surface below renders and behaves exactly as before the story.
     *
     * Gates: My Day's operational grouping (FE-09, FE-15) and its group header (FE-21), the row heights
     * (FE-26), the `myDayTasks.v2` cache key (FE-28), the status-pill renderer (FE-29), the Task Action
     * Banner store (FE-50 — `vm$` stays null), the completion resolver (FE-65, rule R0), the Lead Next
     * Steps shells (FE-78, FE-84), the completion hosts (FE-87, FE-94, FE-97, FE-99, FE-101, FE-102) and
     * the bulk completion flow (FE-115).
     *
     * The backend has a matching flag (`MyDayLeadCommsConsts.FeatureEnabled`) that gates
     * `TaskContext/ReadTaskContexts`, `EmailAutomation/CheckReplyBeforeComplete`, the bulk preflight /
     * undo / lead-stage endpoints and the already-completed no-op — keep the two in sync, and flip the
     * backend FIRST (it is a compiled constant: rebuild + redeploy). Deliberately NOT gated on either
     * side: the task-fact stamps, the My Day row facts and the new SignalR card keys, which must accrue
     * before the flip. The backend flag also holds back the two server changes today's screens would
     * show: SendReply's new card (while off it sends today's card, only when the send cleared the
     * reply flag; the FE-39 merge fixes in the plan must ship first) and the person-only association
     * fill of My Day / Watching rows (today's grid would move those rows into Follow Ups; the U4 group
     * resolver, FE-09, is what consumes it).
     */
    // P8 flip (CFG-06), 2026-10-12: ON in checked-in code for QA on dev and test (user). A compiled const: ANY build of this
    // code turns the feature on - set it back to `false` before a production deploy unless prod should get it too.
    static readonly myDayLeadCommsFeatureEnabled = true;

    /**
     * P7 (CFG-03, MD-D38; story §82-§85, §101): the FE half of the AI Next Action kill switch, beside the BE's
     * `MyDayLeadCommsConsts.AiNextActionEnabled`. On (with `myDayLeadCommsFeatureEnabled`), the task banner asks for the
     * server's AI action, and the headline of a Primary Lead Task or a non-Lead Email task may become its text (PR28,
     * 2026-10-12: Leads too, overriding §64); guidance, metadata and the rail never do, and with no fresh AI text the
     * deterministic headline stays (§83). Off = exactly P3's banner.
     * ON since the P8 flip (2026-10-12, dev / test QA) - the same production caveat as the flag above.
     */
    static readonly myDayAiNextActionEnabled = true;

    /**
     * Master switch for the Lead Pipeline epic (LEAD_PIPELINE_PLAN.md at the repo root, A9). FALSE in checked-in code
     * until its QA flip; while it is off every surface renders and behaves exactly as before the epic.
     *
     * LP1 gates (the Stages tab of "Types, stages & more"): the "Suggest next step" block, the D3 follow-up mode
     * (unset = 5 days ticked; unticked = No follow-up), the R3 "Won = Hide after 30 days" default, the status-change and
     * off-board-type warnings, the blocked delete dialog and the new help copy. Off, the save sends none of the new fields,
     * so the server keeps whatever they hold.
     *
     * The backend has a matching flag (`LeadPipelineConsts.FeatureEnabled`) - keep the two in sync, and flip the backend
     * FIRST (a compiled constant: rebuild + redeploy).
     */
    static readonly leadPipelineFeatureEnabled = true;

    /**
     * Configurable lost reasons (LEAD_LOST_REASONS_PLAN.md at the repo root): the Lost reasons tab of "Types, stages & more", the
     * tenant's reasons in every "Mark lost" picker (6 + more, the Other note, the require switch, stage defaults), bulk Same / One by
     * one and Change / Add reason after Lost. Effective only with `leadPipelineFeatureEnabled` (`lostReasonsEnabled()`); off = MD's six
     * fixed reasons everywhere, exactly as before.
     *
     * The backend twin is `LostReasonsConsts.FeatureEnabled` - keep the two in sync and flip the backend FIRST. TRUE in checked-in
     * code by the user's choice (2026-10-22, like the LP flags): set it to false before a production deploy unless prod should get it.
     */
    static readonly lostReasonsFeatureEnabled = true;

    /**
     * Adaptive Onboarding capability table (build-spec P0-05; A1 / D29). Keys are the plan's `capabilityKey` values (epic
     * §39.4). A capability that is true here may be shown as CURRENT on the onboarding Email and Prove-it cards; one that is
     * false or missing is only ever shown as "Proposed" / "Suggested later" (OB-M-11).
     *
     * Twin of the backend `OnboardingCapabilities` (Core.Shared/Onboarding/OnboardingCapabilities.cs) - keep the two in sync
     * (OnboardingFixtureProvider_Tests pins the keys and values). primaryLeadTaskConsolidation follows the Lead Pipeline
     * (`leadPipelineFeatureEnabled` / `LeadPipelineConsts.FeatureEnabled`). livingLeadSummary follows it too and also needs
     * the backend's `LeadPipelineConsts.LivingSummaryJobEnabled` (no Angular twin; true in code). The rest are planned.
     */
    static readonly onboardingCapabilities: Readonly<Record<string, boolean>> = {
        primaryLeadTaskConsolidation: AppConsts.leadPipelineFeatureEnabled,
        participantBasedWorkMatching: false,
        emailToWorkFieldUpdate: false,
        emailToCalendarEvent: false,
        livingLeadSummary: AppConsts.leadPipelineFeatureEnabled,
        leadToWorkHandoff: false
    };

    /**
     * Master switch for grid editing on the project-template composer's step-2 task grid
     * ("Edit in grid view" in the step-2 toolbar — SECTIONS_ROLES_GRID_PLAN.md phase 6). This is
     * the design's THIRD grid-edit scene, alongside the project task grid and the all-projects
     * list that `gridEditFeatureEnabled` above already covers.
     *
     * ⚠️ NOT the same flag as `gridEditFeatureEnabled`. That one gates the two grids that write to
     * the server cell by cell. This one gates a grid over the composer's IN-MEMORY draft, which is
     * persisted by the ordinary whole-template save when the wizard is saved — so its Done button
     * dispatches nothing and no endpoint is involved.
     *
     * ⚠️ FE-ONLY, DELIBERATELY. `TrTaskTemplate` already carries all seven editable columns, so
     * there is no new endpoint, no new procedure and nothing for a backend flag to guard.
     */
    static readonly templateGridEditFeatureEnabled = true;

    /**
     * Master switch for the ghost "Add task template" row at the foot of the PROJECT-TEMPLATE
     * COMPOSER's step-2 grid (SECTIONS_ROLES_GRID_PLAN.md phase 7, vfh6 TrovveProjects.dc.html
     * :2255). That composer row is the ONLY thing this flag gates.
     *
     * ⚠️ It briefly also gated an equivalent add row on the project task grid (phase 8). That row
     * was an INVENTION — handoff 6 gives an add row to the composer only, and both project grids
     * go straight from their last row to the footer note — and it was REMOVED on request rather
     * than signed off. Nothing on the project side reads this flag: the project grid's add row, which
     * the user DID ask for on 2026-09-28, has its own switch below.
     *
     * Stacks with `templateGridEditFeatureEnabled`: the composer's grid edit must be on for its
     * add row to be reachable.
     */
    static readonly gridAddRowFeatureEnabled = true;

    /**
     * Master switch for the ghost "Add task" row on the PROJECT task grid's grid editing (the project
     * panel's Tasks tab > Edit in grid view). Asked for by the user on 2026-09-28 ("in edit grid view allow
     * adding a task in view project, see design in templates > tasks step"): the composer's step-2 add row
     * and its Excel-style entry, on the project grid. A row added there is created as a real task when Done
     * is pressed, through `Task/CreateTask` (PROJECT_SETTINGS_GRID_ADD_TEST_CASES.md, section D).
     *
     * FE-ONLY: no new endpoint. Stacks with `gridEditFeatureEnabled` - the grid must exist for its add row
     * to be reachable. Set to FALSE and the project grid renders exactly as before, with no add row.
     */
    static readonly projectGridAddRowFeatureEnabled = true;

    /**
     * Master switch for the role PRIMARY person (SECTIONS_ROLES_GRID_PLAN.md phase 9,
     * stories/role-primary-person.md): the amber star on a person's chip in the new-project wizard's
     * Team step and on a member row in Project settings → Members, plus the **Primary** tag beside
     * the primary of any multi-person role. Set to FALSE and both surfaces render exactly as they do
     * today — no star, no tag, no hint line.
     *
     * Naming is fixed by the story: the setting is **Primary** and the person is the role's
     * *primary*. Never "favourite"; never "owner", which already means something else in project
     * members.
     *
     * The backend has a matching flag (`RolePrimaryConsts.FeatureEnabled`) which makes
     * `Task/CreateOrUpdateProjectMembers` ignore the `primaryUserId` field, so a stale frontend
     * cannot write a primary through it — **keep the two in sync**.
     *
     * ⚠️ The control only exists for a role holding TWO OR MORE people (story rule 1). A role with
     * one person has an implicit primary and shows nothing, regardless of this flag.
     *
     * ⚠️ The `isPrimary` flag that comes back on every member row is NOT gated and does not need to
     * be. Nothing is back-filled: it is computed server-side as `IsPrimary DESC, Id ASC`, so a role
     * with nobody flagged resolves to its earliest-added member — which is story rule 4 and is
     * already this app's shipped behaviour. With the flag off the field is simply true of the person
     * the work would have gone to anyway.
     */
    static readonly rolePrimaryFeatureEnabled = true;

    static remoteServiceBaseUrl: string;
    static remoteServiceBaseUrlFormat: string;
    static appVersion: string;
    static appBaseUrl: string;
    static appBaseHref: string; // returns angular's base-href parameter value if used during the publish
    static appBaseUrlFormat: string;
    static subscriptionExpireNootifyDayCount: number;
    static authority = 'https://login.microsoftonline.com/common/';
    // Basic identity scopes — used for Login and Sign Up only
    static scopes: string[] = [
        'openid',
        'profile',
        'offline_access',
        'user.read'
    ];

    // Full Graph scopes — used exclusively for the Connect (Microsoft 365) button.
    // Must match the backend GraphApis:Scope in appsettings so the token exchange succeeds.
    // NOTE: Presence.Read.All, Notes.Read.All and Sites.Manage.All require admin consent.
    //       If the tenant admin has not pre-consented, non-admin users will get
    //       consent_required and the popup flow will show the admin consent modal
    //       (see _openConsentPopup in each consuming component).
    static fullScopes: string[] = [
        'openid', 
        'profile', 
        'offline_access', 
        'email', 
        'user.read', 
        'User.ReadBasic.All', 
        'People.Read', 
        'Calendars.ReadWrite', 
        'Calendars.ReadWrite.Shared', 
        'Tasks.ReadWrite', 
        'Presence.Read.All', 
        'Files.ReadWrite.All', 
        'Sites.Read.All', 
        'Sites.ReadWrite.All', 
        'Sites.Manage.All', 
        'Contacts.ReadWrite', 
        'Mail.Read', 
        'Mail.ReadWrite', 
        'Mail.ReadWrite.Shared', 
        'Mail.Send', 
        'Mail.Send.Shared', 
        'MailboxSettings.Read', 
        'Team.Create', 
        'Team.ReadBasic.All', 
        'TeamSettings.ReadWrite.All', 
        'TeamMember.ReadWrite.All', 
        'Channel.Create', 
        'Channel.ReadBasic.All', 
        'ChannelSettings.ReadWrite.All', 
        'ChannelMember.ReadWrite.All', 
        'ChannelMessage.Read.All', 
        'ChannelMessage.Send', 
        'Chat.ReadWrite', 
        'ChatMessage.Send'
    ];

    // User-level Graph scopes — identical to fullScopes but without the permissions
    // that require admin consent (Presence.Read.All, Notes.Read.All, Sites.Manage.All).
    // Used automatically as a fallback when a non-admin user triggers the consent popup.
    static userScopes: string[] = [
        'openid', 
        'profile', 
        'offline_access', 
        'email', 
        'user.read', 
        'User.ReadBasic.All', 
        'People.Read', 
        'Calendars.ReadWrite', 
        'Calendars.ReadWrite.Shared', 
        'Tasks.ReadWrite', 
        'Presence.Read.All', 
        'Files.ReadWrite.All', 
        'Sites.Read.All', 
        'Sites.ReadWrite.All', 
        'Sites.Manage.All', 
        'Contacts.ReadWrite', 
        'Mail.Read', 
        'Mail.ReadWrite', 
        'Mail.ReadWrite.Shared', 
        'Mail.Send', 
        'Mail.Send.Shared', 
        'MailboxSettings.Read', 
        'Team.Create', 
        'Team.ReadBasic.All', 
        'TeamSettings.ReadWrite.All', 
        'TeamMember.ReadWrite.All', 
        'Channel.Create', 
        'Channel.ReadBasic.All', 
        'ChannelSettings.ReadWrite.All', 
        'ChannelMember.ReadWrite.All', 
        'ChannelMessage.Read.All', 
        'ChannelMessage.Send', 
        'Chat.ReadWrite', 
        'ChatMessage.Send'
    ];

    static canAutoLogin = false;
    // Admin consent redirect URI — must be registered in Azure App Registration reply URLs
    static adminConsentRedirectUri = '/account/admin-consent-callback';
    static localeMappings: any = [];

    static readonly userManagement = {
        defaultAdminUserName: 'admin'
    };

    static readonly localization = {
        defaultLocalizationSourceName: 'Vineforce'
    };

    static readonly authorization = {
        encrptedAuthTokenName: 'enc_auth_token'
    };

    static readonly grid = {
        defaultPageSize: 30,
        loadMorePageSize: 20,
        maxResultCount: 2147483647
    };
    static readonly toastrOption = {
        closeButton: false,
        toastClass: 'toast',
        debug: false,
        newestOnTop: false,
        progressBar: false,
        positionClass: 'toast-bottom-full-width',
        preventDuplicates: true,
        onclick: null,
        showDuration: '300',
        hideDuration: '1000',
        timeOut: 5000,
        extendedTimeOut: 1000,
        showEasing: 'swing',
        hideEasing: 'linear',
        showMethod: 'fadeIn',
        hideMethod: 'fadeOut',
        tapToDismiss: false,
        enableHtml: true,
        maxOpened: 1
    };
    static readonly toastrOptionWithUndoBtn = {
        closeButton: false,
        toastClass: 'toast',
        debug: false,
        newestOnTop: false,
        progressBar: false,
        positionClass: 'toast-bottom-full-width',
        preventDuplicates: true,
        onclick: null,
        showDuration: '300',
        hideDuration: '1000',
        timeOut: 5000,
        extendedTimeOut: 1000,
        showEasing: 'swing',
        hideEasing: 'linear',
        showMethod: 'fadeIn',
        hideMethod: 'fadeOut',
        tapToDismiss: false,
        enableHtml: true,
        maxOpened: 1,
        buttons: [{
            id: '1',
            title: 'Undo'
        }]
    };
    static readonly toastrOptionWithCloseBtn = {
        closeButton: false,
        toastClass: 'toast',
        debug: false,
        newestOnTop: false,
        progressBar: false,
        positionClass: 'toast-bottom-full-width',
        preventDuplicates: true,
        onclick: null,
        showDuration: '300',
        hideDuration: '1000',
        timeOut: 5000,
        extendedTimeOut: 1000,
        showEasing: 'swing',
        hideEasing: 'linear',
        showMethod: 'fadeIn',
        hideMethod: 'fadeOut',
        tapToDismiss: false,
        enableHtml: true,
        maxOpened: 1,
        buttons: [{
            id: '2',
            title: 'Close'
        }]
    };
    static readonly uploadFileSize = 25000000;

    static readonly maxSourceData = 1000;

    static readonly maxTypeAheadResults = 5;

    static readonly requiredCharactersForSearch = 2;

    /**
     * Stands in for TrUserPinnedEntities.Id between clicking pin and AddFavorite answering with the
     * real one. A row renders "pinned" from having a pin id at all, so the flag has to be a number —
     * and it is negative so that a second click before the response can be told apart from a real id
     * and skip the remove call, instead of asking the server to delete a row that does not exist.
     * The magic 123123321 this replaces was indistinguishable from one.
     */
    static readonly pendingPinId = -1;

    static readonly fieldLimits = {
        team: 100
    };

    static readonly mobileScreenWidth = 525;
    static teamAuthCode = '';
    static O365QueryParams = {
        clientId: environment.clientID,
        responseType: 'code',
        redirectUri: 'http://localhost:22742/token/token',
        // Default to basic identity scopes only.
        // The Connect M365 flow overrides this from the backend (getApiSettingList → i.scope)
        // before building the OAuth URL, so this initial value is only a safety fallback.
        scope: AppConsts.scopes.join(' '),
        responseMode: 'form_post',
        nonce: 678910,
        state: '',
        graphApiAuthorizeUrl: ''
    };
    static isUserNotExists = false;
    static isTenantNotExists = false;

    static readonly oneDriveErrors: Record<string, { message: string; cancelLabel?: string; saveLabel?: string }> = {
        '101': {
            message: 'A file with this name already exists in this location. Please choose a different name',
            cancelLabel: 'Cancel',
            saveLabel: 'Rename'
        },
        '102': {
            message: 'The file or folder no longer exists. It may have been moved or deleted.',
            saveLabel: 'OK'
        },
        '103': {
            message: 'You don\'t have permission to perform this action in this location. Please contact your administrator for access.',
            cancelLabel: 'Close',
            saveLabel: 'OK'
        },
        '104': {
            message: 'The destination path cannot be the same as the source object itself.',
            cancelLabel: 'Close',
            saveLabel: 'OK'
        },
        '105': {
            message: 'Schema mismatch detected. Please hover over the file, open in its location, and manually move it from SharePoint.',
            cancelLabel: 'Close',
            saveLabel: 'OK'
        },
        '106': {
            message: 'The file is currently open and locked for editing, preventing the move operation from being successful. Please close the file or ensure it is not being edited, then try moving it again.',
            cancelLabel: 'Cancel',
            saveLabel: 'Retry move'
        },
        '107': {
            message: 'Someone changed this file while you were working on it. Please refresh and try again.',
            cancelLabel: 'Close',
            saveLabel: 'Try again'
        },
        '108': {
            message: 'This file is under a retention policy or legal hold and cannot be modified.',
            cancelLabel: 'Close',
            saveLabel: 'OK'
        },
        '109': {
            message: 'You can\'t share this with people outside your organization. Please contact your admin about external sharing.',
            cancelLabel: 'Close',
            saveLabel: 'OK'
        },
        '110': {
            message: 'External sharing is disabled for this location. Please contact your administrator.',
            cancelLabel: 'Close',
            saveLabel: 'OK'
        },
        '111': {
            message: 'The file was modified during the operation. Please try again.',
            cancelLabel: 'Close',
            saveLabel: 'Try again'
        },
        '112': {
            message: 'The destination storage quota has been reached. Please free up space and try again.',
            cancelLabel: 'Close',
            saveLabel: 'OK'
        },
        '113': {
            message: 'This file has been flagged as malware and cannot be processed.',
            saveLabel: 'OK'
        },
        '114': {
            message: 'Microsoft 365 is currently rate limiting requests. Please wait a moment and try again.',
            cancelLabel: 'Close',
            saveLabel: 'Try again'
        },
        // 115-119: the failures that used to reach users as Graph's raw text ("General exception while
        // processing") or as a bare number. Classified in OneDriveAppService.ClassifyServiceException.
        '115': {
            message: 'Microsoft 365 could not complete the request. This usually means the library has restrictions '
                + '(required columns, content approval, a locked site) or SharePoint hit a temporary error. '
                + 'Please try again; if it keeps failing, contact your SharePoint administrator.',
            cancelLabel: 'Close',
            saveLabel: 'Try again'
        },
        '116': {
            message: 'Your Microsoft 365 account is not connected. Please connect it and try again.',
            cancelLabel: 'Close',
            saveLabel: 'OK'
        },
        '117': {
            message: 'SharePoint rejected the request. Please check the name or location and try again.',
            cancelLabel: 'Close',
            saveLabel: 'OK'
        },
        '118': {
            message: 'Microsoft 365 could not be reached. Please try again in a moment.',
            cancelLabel: 'Close',
            saveLabel: 'Try again'
        },
        '119': {
            message: 'Microsoft 365 reported an unexpected error. Please try again.',
            cancelLabel: 'Close',
            saveLabel: 'Try again'
        },
    };

}
